// @flow

import sum from 'lodash/fp/sum';

import { type Variation } from './product';
import type { AllocationStrategy } from './inventory';

import {
  type ResolvedAllocationStrategy,
  type StockShipment,
  resolvedAllocationStrategyIsUnlimitedSelection,
  resolvedAllocationStrategyIsUnlimitedAllocation,
  earliestQuantityAvailableDateInStockShipments,
} from 'modules/inventory';
import { toDate, isBefore } from 'modules/date';
import {
  type StockItemInWarehouseAtDate,
  type StockItemAtDate,
} from 'modules/product-with-inventory';

/**
 * Functions having to do with how to display information about product inventory.
 *
 * @module modules/availability
 */

/**
 * AvailabilityLevel is a low/medium/high rough visual indicator of how much
 * availability there is for a given item, for quick visual scanning.
 *
 * @typedef {'indeterminate'|'low'|'medium'|'high'|'infinity'} AvailabilityLevel
 */
export type AvailabilityLevel =
  | 'indeterminate'
  | 'low'
  | 'medium'
  | 'high'
  | 'infinity'
  | 'futureAvailability';

/**
 * @constant
 * @type {AvailabilityLevel[]}
 * @default
 */
export const AVAILABILITY_LEVELS: $ReadOnlyArray<AvailabilityLevel> = [
  'indeterminate',
  'low',
  'medium',
  'high',
  'infinity',
  'futureAvailability',
];

/**
 * The default highest quantity of units shown to the user
 *
 * @constant
 * @type {number}
 * @default
 */
export const DEFAULT_LABEL_MAX: 99 = 99;

/**
 * The default template for the label when the available quantity is above the
 * `labelMax`. `${max}` gets replaced with the `labelMax` and '${quantity}'
 * gets replaced with the current quantity.
 *
 * @constant DEFAULT_LABEL_HIGH
 * @type {string}
 * @default
 */
// eslint-disable-next-line no-template-curly-in-string
export const DEFAULT_LABEL_HIGH: '${max}+' = '${max}+';

/**
 * The default maximum value that will result in a 'low' level.
 *
 * @constant DEFAULT_LOW_LIMIT
 * @type {string}
 * @default
 */
export const DEFAULT_LOW_LIMIT: 0 = 0;

/**
 * The default maximum value that will result in a 'medium' level.
 *
 * @constant DEFAULT_MEDIUM_LIMIT
 * @type {string}
 * @default
 */
export const DEFAULT_MEDIUM_LIMIT: 19 = 19;

/**
 * Returns the `AvailabilityLevel` which matches the given `quantity`.
 */
export const levelForQuantity = (quantity: number): AvailabilityLevel => {
  if (quantity <= DEFAULT_LOW_LIMIT) return 'low';
  if (quantity <= DEFAULT_MEDIUM_LIMIT) return 'medium';
  return 'high';
};

/**
 * Creates a label from a simple template format for labels when the
 * available quantity > `labelMax`.
 */
export const highLabel =
  (labelMax: number, labelHigh: string): ((quantity: number) => string) =>
  (quantity: number): string =>
    labelHigh
      // eslint-disable-next-line no-template-curly-in-string
      .replace('${max}', labelMax.toString())
      // eslint-disable-next-line no-template-curly-in-string
      .replace('${quantity}', quantity.toString());

/**
 * Creates the label the user will see to indicate how many units they may
 * theoretically order.
 */
export const labelForQuantity =
  (labelMax: number, labelHigh: string): ((quantity: number) => string) =>
  (quantity: number): string =>
    quantity > labelMax
      ? highLabel(labelMax, labelHigh)(quantity)
      : String(quantity);

export const decorate = (strategy: ResolvedAllocationStrategy): boolean =>
  ['backorder', 'shipmentsByAvailableStock', 'unrestricted'].includes(strategy);

export const labelDecoratedForStrategy =
  (
    strategy: ResolvedAllocationStrategy,
    hasFutureUnallocated: boolean,
  ): ((label: string) => string) =>
  (label: string): string =>
    hasFutureUnallocated && decorate(strategy) && !label.match(/\+$/)
      ? `${label}+`
      : label;

export const labelForQuantityAndStrategy =
  (
    labelMax: number,
    labelHigh: string,
  ): ((
    quantity: number,
    strategy: ResolvedAllocationStrategy,
    hasFutureUnallocated: boolean,
  ) => string) =>
  (
    quantity: number,
    strategy: ResolvedAllocationStrategy,
    hasFutureUnallocated: boolean,
  ): string =>
    resolvedAllocationStrategyIsUnlimitedAllocation(strategy)
      ? ''
      : labelDecoratedForStrategy(
          strategy,
          hasFutureUnallocated,
        )(labelForQuantity(labelMax, labelHigh)(quantity));

export const levelForQuantityAndStrategy = (
  quantity: number,
  strategy: ResolvedAllocationStrategy,
  hasFutureUnallocated: boolean,
): AvailabilityLevel => {
  if (
    hasFutureUnallocated &&
    strategy === 'unrestricted' &&
    quantity <= DEFAULT_LOW_LIMIT
  ) {
    return 'futureAvailability';
  }
  return strategy === 'unlimited' ? 'infinity' : levelForQuantity(quantity);
};

export const level = (
  strategy: AllocationStrategy,
  resolvedStrategy: ResolvedAllocationStrategy,
  isReleased: boolean,
  quantity: number,
  hasFutureUnallocated: boolean,
): AvailabilityLevel => {
  if (
    hasFutureUnallocated &&
    strategy.type === 'unrestricted' &&
    quantity <= DEFAULT_LOW_LIMIT
  ) {
    return 'futureAvailability';
  }
  return resolvedStrategy === 'unlimited' &&
    !isReleased &&
    strategy.type !== 'dateWindow'
    ? 'medium'
    : levelForQuantityAndStrategy(
        quantity,
        resolvedStrategy,
        hasFutureUnallocated,
      );
};

export const shouldShowBackorderWarning = (
  strategy: ResolvedAllocationStrategy,
): boolean => strategy === 'backorder';

export const availabilityMessageDate =
  (
    enforceReleaseDate?: boolean,
  ): ((
    strategy: ResolvedAllocationStrategy,
    unallocatedStock: $ReadOnlyArray<StockShipment>,
    variation: Variation,
    arriveOnDate: Date,
  ) => Date | null) =>
  (
    strategy: ResolvedAllocationStrategy,
    unallocatedStock: $ReadOnlyArray<StockShipment>,
    variation: Variation,
    arriveOnDate: Date,
  ): Date | null => {
    if (resolvedAllocationStrategyIsUnlimitedSelection(strategy)) {
      return variation.available_on ? toDate(variation.available_on) : null;
    }

    const firstDateWithInventory =
      earliestQuantityAvailableDateInStockShipments(unallocatedStock);

    if (firstDateWithInventory === null) return null;

    const variationAvailableOn = toDate(variation.available_on || null);

    const possiblyRestrictedDate =
      enforceReleaseDate && firstDateWithInventory < variationAvailableOn
        ? variationAvailableOn
        : firstDateWithInventory;

    return possiblyRestrictedDate &&
      isBefore(arriveOnDate, possiblyRestrictedDate)
      ? possiblyRestrictedDate
      : null;
  };

export const totalQuantity = (
  stockItemsAtDate: $ReadOnlyArray<
    StockItemInWarehouseAtDate | StockItemAtDate,
  >,
): number =>
  sum(
    stockItemsAtDate.map((stockItemDate) => stockItemDate.selectableQuantity),
  );
